// ⌘K Ask-anywhere (Phase 3.5e) — the interface starts disappearing.
//
// Autopilot's chat, reachable from EVERY authenticated screen with one
// keystroke (⌘K / Ctrl+K), like Claude's own palette. The destination is
// not a better Autopilot page — it's that talking to the OS becomes the
// primary interface and pages become the fallback.
//
// Reuses AskEuphoricPanel wholesale: same role-scoped answers, evidence
// panels, action chips, lexicon. The thread lives for the session, so
// reopening the palette continues the conversation. Hidden on the pages
// that already ARE the chat (Autopilot, /ask).

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Command, Sparkles } from 'lucide-react';
import AskEuphoricPanel from './AskEuphoricPanel';
import { useAuth } from '../context/AuthContext';

const EXCLUDED_ROLES = ['client', 'affiliate_partner'];

export default function AskAnywherePalette() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!user || EXCLUDED_ROLES.includes(user.role)) return null;
  // The chat pages already are the palette — don't stack a second one.
  if (location.pathname.startsWith('/admin/autopilot')
      || location.pathname.startsWith('/ask')) return null;

  // The VISIBLE doorway. ⌘K is the power-user path, but most staff are not
  // keyboard people (and phones have no ⌘ at all) — a floating Ask button
  // on every page is how everyone else discovers it. The shortcut hint on
  // it teaches the fast way over time.
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Ask Euphoric (⌘K)"
        className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 pl-3.5 pr-4 py-2.5 rounded-full bg-slate-900 text-white shadow-xl shadow-slate-900/20 hover:bg-slate-800 hover:shadow-2xl transition-all"
      >
        <Sparkles size={15} className="text-teal-300" />
        <span className="text-[13px] font-medium">Ask</span>
        <span className="hidden sm:inline-flex items-center gap-0.5 text-[10px] text-slate-400 border border-slate-700 rounded px-1 py-0.5">
          <Command size={8} />K
        </span>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center pt-[8vh] px-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-[760px] h-[74vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 h-11 px-4 flex items-center justify-between border-b border-slate-100">
          <p className="text-[12px] text-slate-500 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400 border border-slate-200 rounded px-1 py-0.5">
              <Command size={9} />K
            </span>
            Ask Euphoric — from anywhere
          </p>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 -mr-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <X size={15} />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <AskEuphoricPanel variant="page" showBriefing={false} />
        </div>
      </div>
    </div>
  );
}
